<template>
  <div>
    <el-row
      type="flex"
      justify="center"
    >
      <el-col :span="12">
        <BulkCall
          ref="bulk"
          :function="writeRow"
          :arguments="batchArguments"
          hide-results
          @callCompleted="record"
          @completed="finish"
        >
          <!-- The row being written and the time left, on the scale of the
               el-result that takes its place. -->
          <div
            v-if="isRunning"
            slot="progress"
            slot-scope="{ completed, total, percentage }"
            class="write-step__progress"
          >
            <div
              aria-live="polite"
              class="write-step__progress-title"
            >
              {{
                $t(`${I18N}.progress.title`, {
                  current: Math.min(completed + 1, total),
                  total,
                })
              }}
            </div>
            <el-progress
              :percentage="percentage"
              :stroke-width="12"
              :show-text="false"
              class="write-step__bar"
            />
            <el-row
              type="flex"
              justify="space-between"
              class="write-step__progress-detail"
            >
              <span>{{ inFlight(completed) }}</span>
              <span>{{ progressDetail(completed, total) }}</span>
            </el-row>
          </div>
        </BulkCall>
        <el-result
          v-if="!isRunning"
          :icon="resultIcon"
          :title="$tc(`${I18N}.written`, counts.written)"
          :sub-title="resultDetail"
          aria-live="polite"
        />
      </el-col>
    </el-row>

    <template v-if="!isRunning && failedRows.length > 0">
      <!-- `table-wrapper` gives the table the theme's dark rows, as elsewhere. -->
      <div class="table-wrapper">
        <el-table
          :data="failedRows"
          row-key="key"
          max-height="400"
        >
          <el-table-column
            :label="$t(`${SUMMARY}.columns.sample`)"
            min-width="170"
          >
            <template slot-scope="{ row }">
              <div>{{ row.row.labSampleCode }}</div>
              <div class="write-step__secondary">
                {{
                  $t(`${SUMMARY}.columns.sourceRow`, {
                    row: row.row.sourceRow,
                    column: row.row.column,
                  })
                }}
              </div>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t(`${SUMMARY}.columns.analysis`)"
            min-width="200"
          >
            <template slot-scope="{ row }">
              <div>{{ row.row.analysisCode }}</div>
              <div class="write-step__secondary">
                {{ row.row.analysisDescription }}
              </div>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t(`${SUMMARY}.columns.value`)"
            prop="row.value"
            width="130"
          />
          <el-table-column
            :label="$t(`${I18N}.columns.error`)"
            prop="error"
            min-width="300"
          />
        </el-table>
      </div>

      <!-- The one action of the step, on the side of the dialog's actions. -->
      <el-row
        type="flex"
        justify="end"
        class="write-step__actions"
      >
        <el-button
          type="primary"
          icon="el-icon-refresh"
          @click="retry"
        >
          {{ $t(`${I18N}.retry`) }}
        </el-button>
      </el-row>
    </template>
  </div>
</template>

<script>
  import moment from 'moment';
  import { samplesApi } from '@/api';
  import BulkCall from '@/components/BulkCall.vue';
  import {
    FILE_IMPORT_SOURCE,
    remainingMs,
    writeBody,
    writeCounts,
  } from './writeRows';

  // Step 4 (FE-6). Writes the rows as soon as it opens, one PATCH each, and
  // lets the failed ones be written again. Emits `writing` when a write loop
  // starts (retries included) and `completed` when it ends, with the rows
  // written so far and the ones still failed.
  export default {
    name: 'WriteStep',
    components: { BulkCall },
    props: {
      rows: { type: Array, required: true },
      input: { type: Object, required: true },
    },
    data() {
      return {
        I18N: 'analysesData.importValues.write',
        SUMMARY: 'analysesData.importValues.summary',
        // Indexes in `rows` of the current loop.
        batch: [],
        // By index in `rows`: null once written, the error message otherwise.
        outcomes: {},
        isRunning: true,
        // Of the current loop, for the time left.
        startedAt: 0,
        elapsedMs: 0,
      };
    },
    computed: {
      batchArguments() {
        return this.batch.map((key) => [key]);
      },
      counts() {
        return writeCounts(this.rows, this.outcomes);
      },
      failedRows() {
        return Object.keys(this.outcomes)
          .filter((key) => this.outcomes[key] !== null)
          .map((key) => ({
            key: Number(key),
            row: this.rows[key],
            error: this.outcomes[key],
          }));
      },
      resultIcon() {
        if (this.counts.failed === 0) return 'success';
        return this.counts.written === 0 ? 'error' : 'warning';
      },
      resultDetail() {
        const { overwritten, failed } = this.counts;
        return [
          overwritten > 0 && this.$tc(`${this.I18N}.overwritten`, overwritten),
          failed > 0 && this.$tc(`${this.I18N}.failed`, failed),
        ]
          .filter(Boolean)
          .join(' · ');
      },
    },
    mounted() {
      this.start(this.rows.map((row, key) => key));
    },
    methods: {
      start(keys) {
        keys.forEach((key) => this.$delete(this.outcomes, key));
        this.batch = keys;
        this.isRunning = true;
        this.startedAt = Date.now();
        this.elapsedMs = 0;
        this.$emit('writing');
        // BulkCall reads the new arguments once rendered.
        this.$nextTick(() => this.$refs.bulk.run());
      },
      retry() {
        this.start(this.failedRows.map(({ key }) => key));
      },
      writeRow(key) {
        const row = this.rows[key];
        return samplesApi.updateSampleOperationValues(
          row.operationValueId,
          writeBody(row, this.input),
          FILE_IMPORT_SOURCE
        );
      },
      record({ index, error }) {
        this.$set(this.outcomes, this.batch[index], error);
        this.elapsedMs = Date.now() - this.startedAt;
      },
      // The sample and analysis of the row being written.
      inFlight(completed) {
        const row = this.rows[this.batch[completed]];
        return row ? `${row.labSampleCode} · ${row.analysisCode}` : '';
      },
      // Failures as they come, then the time left once it can be told.
      progressDetail(completed, total) {
        const left = remainingMs(this.elapsedMs, completed, total);
        const { failed } = this.counts;
        return [
          failed > 0 && this.$tc(`${this.I18N}.failed`, failed),
          left !== null &&
            this.$t(`${this.I18N}.progress.remaining`, {
              time: moment.duration(left).humanize(),
            }),
        ]
          .filter(Boolean)
          .join(' · ');
      },
      finish() {
        this.isRunning = false;
        const { written, failed } = this.counts;
        this.$emit('completed', { written, failed });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .write-step__secondary {
    color: $--color-text-regular;
    font-size: 0.85em;
    line-height: 1.4;
  }

  .write-step__progress {
    padding: $--result-padding;
  }

  .write-step__progress-title {
    color: $--color-text-primary;
    font-size: $--result-title-font-size;
    line-height: 1.3;
  }

  .write-step__bar {
    margin: 1rem 0 0.75rem;
  }

  .write-step__progress-detail {
    color: $--color-text-regular;
    font-size: $--font-size-base;
    line-height: 1.3;
  }

  .write-step__actions {
    margin-top: 1rem;
  }
</style>
